import { CalendarDate, parseDate } from "@internationalized/date";

export type ExperienceStrategyView = "overview" | "timeline";
export type ExperienceStrategyDateMode = "day" | "range";

export interface ExperienceStrategyPageQuery {
  dateMode: ExperienceStrategyDateMode;
  from: CalendarDate | null;
  search: string;
  shopBindingId: string;
  to: CalendarDate | null;
  view: ExperienceStrategyView;
}

export interface ExperienceStrategyTimelineQueryArgs {
  dateBefore?: string;
  dateFrom?: string;
  organizationId: string;
  search?: string;
  shopBindingId?: string;
}

function parseCalendarDate(value: string | null) {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
}

function parseShopBindingId(value: string | null) {
  const normalized = value?.trim() ?? "";
  return /^msb_[a-zA-Z0-9_-]{1,156}$/u.test(normalized) ? normalized : "";
}

export function parseExperienceStrategyPageQuery(
  searchParams: URLSearchParams,
): ExperienceStrategyPageQuery {
  const requestedView = searchParams.get("view");
  const requestedMode = searchParams.get("dateMode");
  const dateMode = requestedMode === "range" ? "range" : "day";
  const from = parseCalendarDate(searchParams.get("from"));
  const to = parseCalendarDate(searchParams.get("to"));
  const hasValidRange = Boolean(from && to && from.compare(to) <= 0);

  return {
    dateMode,
    from: hasValidRange ? from : null,
    search: (searchParams.get("q") ?? "").slice(0, 120),
    shopBindingId: parseShopBindingId(searchParams.get("shop")),
    to: hasValidRange ? (dateMode === "day" ? from : to) : null,
    view: requestedView === "overview" ? "overview" : "timeline",
  };
}

export function serializeExperienceStrategyPageQuery(
  query: ExperienceStrategyPageQuery,
) {
  const params = new URLSearchParams();
  if (query.view !== "timeline") params.set("view", query.view);
  if (query.shopBindingId) params.set("shop", query.shopBindingId);
  const normalizedSearch = query.search.trim();
  if (normalizedSearch) params.set("q", normalizedSearch);
  if (query.from && query.to) {
    params.set("dateMode", query.dateMode);
    params.set("from", query.from.toString());
    params.set("to", query.to.toString());
  }
  return params;
}

export function experienceStrategyOverviewPath(shopBindingId: string) {
  const query: ExperienceStrategyPageQuery = {
    dateMode: "day",
    from: null,
    search: "",
    shopBindingId: parseShopBindingId(shopBindingId),
    to: null,
    view: "overview",
  };
  return `/experience-strategies?${serializeExperienceStrategyPageQuery(query).toString()}`;
}

export function safeExperienceStrategyReturnTo(value: unknown) {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    return "";
  }
  try {
    const parsed = new URL(value, "https://merry.local");
    if (parsed.origin !== "https://merry.local" || parsed.pathname !== "/experience-strategies") {
      return "";
    }
    const query = parseExperienceStrategyPageQuery(parsed.searchParams);
    const serialized = serializeExperienceStrategyPageQuery(query).toString();
    return serialized ? `${parsed.pathname}?${serialized}` : parsed.pathname;
  } catch {
    return "";
  }
}

export function toExperienceStrategyTimelineQuery(
  query: ExperienceStrategyPageQuery,
  organizationId: string,
): ExperienceStrategyTimelineQueryArgs {
  const normalizedSearch = query.search.trim();
  return {
    ...(query.from && query.to
      ? {
          dateBefore: query.to.add({ days: 1 }).toString(),
          dateFrom: query.from.toString(),
        }
      : {}),
    organizationId,
    ...(normalizedSearch ? { search: normalizedSearch } : {}),
  };
}

export function formatExperienceStrategyDateFilter(
  query: Pick<ExperienceStrategyPageQuery, "dateMode" | "from" | "to">,
) {
  if (!query.from || !query.to) return "日期筛选";
  const start = `${query.from.year}/${query.from.month}/${query.from.day}`;
  if (query.dateMode === "day" || query.from.compare(query.to) === 0) {
    return start;
  }
  return `${start} - ${query.to.year}/${query.to.month}/${query.to.day}`;
}

export function calendarDateFromParts(
  year: number,
  month: number,
  day: number,
) {
  return new CalendarDate(year, month, day);
}
